import {CreateSubscriptionFormValues} from "../schema-validation";
import {useCreateSubscriptionContext} from "../context/create-subscription";
import {
  formatCreateSubscriptionError,
  useCreateSubscriptionApi,
} from "../../../../react-query-hooks/store/subscriptions/create-subscription";
import {
  createSubscriptionBasicValuesToUpdatePayload,
  createSubscriptionDto,
  mergeSubscriptionTierFormRowsWithApiIds,
  restoreSubscriptionTierApiIdsFromDraft,
} from "../create-subscription-service";
import {CreateSubscriptionApiResponse} from "../../../../react-query-hooks/store/subscriptions/create-subscription/types";
import {useUpdateSubscriptionApi} from "../../../../react-query-hooks/store/subscriptions/update-subscription";
import {formatUpdateSubscriptionError} from "../../../../react-query-hooks/store/subscriptions/update-subscription";
import {normalizeGetSubscriptionResponse} from "../../../../react-query-hooks/store/subscriptions/get-subscription/normalize";
import {useClientV2} from "../../../../utils/hooks";

export type CreateSubscriptionOptions = {
  onSuccess?: (data: CreateSubscriptionApiResponse) => void;
};

function toApiResponseSnapshot(
  normalized: ReturnType<typeof normalizeGetSubscriptionResponse>
): CreateSubscriptionApiResponse {
  const sub = normalized.productSubscription;
  return {
    productSubscription: {
      id: sub.id,
      name: sub.name,
      slug: sub.slug,
      description: sub.description ?? "",
      cover_images: sub.cover_images,
      createdAt: sub.createdAt,
      updatedAt: sub.updatedAt,
      tiny_url: sub.tiny_url,
      tiers: sub.tiers,
    },
  };
}

export function useCreateSubscriptionHook() {
  const {dispatch, state} = useCreateSubscriptionContext();
  const createSubscriptionMutation = useCreateSubscriptionApi();
  const updateSubscriptionMutation = useUpdateSubscriptionApi();
  const client = useClientV2();

  const persistSubscription = async (
    values: CreateSubscriptionFormValues,
    options?: CreateSubscriptionOptions
  ) => {
    const resolvedValues: CreateSubscriptionFormValues = {
      ...values,
      tiers: restoreSubscriptionTierApiIdsFromDraft(values.tiers, state.formData.tiers),
    };
    const existingId = state.apiResponseData?.productSubscription?.id;

    if (!existingId) {
      const data = await createSubscriptionMutation.mutateAsync(
        createSubscriptionDto(resolvedValues)
      );
      const tiers = data.productSubscription.tiers ?? [];
      dispatch({
        type: "save-create-subscription-api-response",
        payload: {
          apiResponseData: {
            productSubscription: {
              id: data.productSubscription.id,
              name: data.productSubscription.name,
              slug: data.productSubscription.slug,
              tiny_url: data.productSubscription.tiny_url,
              cover_images: data.productSubscription.cover_images,
              tiers,
            },
          },
        },
      });
      dispatch({
        type: "save-create-subscription-form-values",
        payload: {
          tiers: mergeSubscriptionTierFormRowsWithApiIds(resolvedValues.tiers, tiers),
        },
      });
      if (options?.onSuccess) {
        options.onSuccess(data);
      } else {
        dispatch({
          type: "update-create-subscription-form-step",
          payload: "coverImagesUpload",
        });
      }
      return;
    }

    const originalTiers = state.apiResponseData?.productSubscription?.tiers ?? [];

    await updateSubscriptionMutation.mutateAsync({
      subscriptionId: existingId,
      payload: createSubscriptionBasicValuesToUpdatePayload(
        resolvedValues,
        originalTiers
      ),
    });

    const envelope = await client(`subscriptions/${existingId}`);
    const normalized = normalizeGetSubscriptionResponse(
      (envelope as {data?: unknown}).data
    );
    const data = toApiResponseSnapshot(normalized);
    const freshTiers = data.productSubscription.tiers ?? [];

    dispatch({
      type: "save-create-subscription-api-response",
      payload: {
        apiResponseData: {
          productSubscription: {
            id: data.productSubscription.id,
            name: data.productSubscription.name,
            slug: data.productSubscription.slug,
            tiny_url: data.productSubscription.tiny_url,
            cover_images: data.productSubscription.cover_images,
            tiers: freshTiers,
          },
        },
      },
    });
    dispatch({
      type: "save-create-subscription-form-values",
      payload: {
        tiers: mergeSubscriptionTierFormRowsWithApiIds(resolvedValues.tiers, freshTiers),
      },
    });

    if (options?.onSuccess) {
      options.onSuccess(data);
    } else {
      dispatch({
        type: "update-create-subscription-form-step",
        payload: "coverImagesUpload",
      });
    }
  };

  const createSubscription = (
    values: CreateSubscriptionFormValues,
    options?: CreateSubscriptionOptions
  ) => {
    void persistSubscription(values, options).catch(() => {
      // Errors surface via mutation query state.
    });
  };

  const createSubscriptionError = createSubscriptionMutation.error
    ? formatCreateSubscriptionError(createSubscriptionMutation.error)
    : updateSubscriptionMutation.error
    ? formatUpdateSubscriptionError(updateSubscriptionMutation.error)
    : undefined;

  const isCreatingSubscription =
    createSubscriptionMutation.isLoading || updateSubscriptionMutation.isLoading;

  return {
    createSubscription,
    createSubscriptionError,
    isCreatingSubscription,
  };
}
